import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LogOut, Shield, Users, Shuffle, Trophy, Dumbbell, ClipboardList, ChevronDown, Check, Settings, KeyRound, UserCircle, Crown, Activity } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import ChangePasswordDialog from "@/components/ChangePasswordDialog";
import { toast } from "sonner";

export default function Navbar() {
  const { user, memberships, switchTeam, logout } = useAuth();
  const navigate = useNavigate();
  const [pwOpen, setPwOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    // Hard-Reload zur Landingpage, damit ProtectedRoute-Redirect zu /login umgangen wird
    window.location.assign("/");
  };

  const handleSwitch = async (teamId) => {
    if (teamId === user?.active_team_id) return;
    const res = await switchTeam(teamId);
    if (res.ok) {
      toast.success(`Mannschaft: ${res.user.active_team_name}`);
      // Volle Reload aller Daten via window.location für saubere Zustandskonsistenz
      window.location.assign("/");
    } else {
      toast.error(res.error || "Wechsel fehlgeschlagen");
    }
  };

  const showSwitcher = memberships.length > 1;

  return (
    <header className="border-b border-zinc-800 bg-zinc-950/80 backdrop-blur sticky top-0 z-30">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <Link to="/dashboard" className="flex items-center gap-3" data-testid="nav-logo-link">
          <div className="h-9 w-9 bg-brand flex items-center justify-center rounded-md">
            <Shield className="h-5 w-5 text-white" strokeWidth={2.5} />
          </div>
          <div className="flex flex-col leading-none">
            <span className="font-display text-lg font-bold tracking-tight text-zinc-100">Trainer Profil</span>
            <span className="text-[11px] tracking-wide text-zinc-500">Spielerverwaltung</span>
          </div>
        </Link>

        {/* Team-Switcher (Desktop in Top-Bar; Mobile-Variante steht eine Zeile tiefer) */}
        {user?.active_team_name && (
          showSwitcher ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="ml-3 hidden md:inline-flex bg-zinc-900 border-zinc-800 hover:bg-zinc-800 hover:border-brand-lighter/50 text-zinc-100 h-9 rounded-md px-3"
                  data-testid="nav-team-switcher-btn"
                >
                  <Users className="h-3.5 w-3.5 mr-2 text-brand-lighter" />
                  <span className="text-xs font-semibold">{user.active_team_name}</span>
                  <ChevronDown className="h-3.5 w-3.5 ml-2 text-zinc-500" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="start"
                className="bg-zinc-900 border-zinc-800 text-zinc-100 min-w-56"
                data-testid="nav-team-switcher-menu"
              >
                <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-zinc-500">
                  Mannschaft wechseln
                </DropdownMenuLabel>
                <DropdownMenuSeparator className="bg-zinc-800" />
                {memberships.map((m) => (
                  <DropdownMenuItem
                    key={m.team_id}
                    onClick={() => handleSwitch(m.team_id)}
                    className="hover:bg-zinc-800 focus:bg-zinc-800 cursor-pointer"
                    data-testid={`nav-team-switch-${m.team_id}`}
                  >
                    <div className="flex items-center justify-between w-full">
                      <div>
                        <div className="text-sm font-medium">{m.team_name}</div>
                        <div className="text-[10px] text-zinc-500">{m.club_name}</div>
                      </div>
                      {m.team_id === user.active_team_id && (
                        <Check className="h-4 w-4 text-brand-lighter" />
                      )}
                    </div>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <div
              className="ml-3 hidden md:flex items-center gap-2 px-3 h-9 rounded-md border border-zinc-800 bg-zinc-900"
              data-testid="nav-team-label"
            >
              <Users className="h-3.5 w-3.5 text-brand-lighter" />
              <span className="text-xs font-semibold text-zinc-100">{user.active_team_name}</span>
            </div>
          )
        )}

        <nav className="hidden md:flex items-center gap-1 mx-4 flex-1 justify-center">
          <NavLink
            to="/dashboard"
            end
            className={({ isActive }) =>
              "flex items-center gap-2 px-3 py-2 text-xs uppercase tracking-wide border-b-2 transition " +
              (isActive
                ? "text-brand-lighter border-brand-lighter"
                : "text-zinc-400 border-transparent hover:text-zinc-100")
            }
            data-testid="nav-dashboard-link"
          >
            <Users className="h-4 w-4" /> Spieler
          </NavLink>
          <NavLink
            to="/team-balance"
            className={({ isActive }) =>
              "flex items-center gap-2 px-3 py-2 text-xs uppercase tracking-wide border-b-2 transition " +
              (isActive
                ? "text-brand-lighter border-brand-lighter"
                : "text-zinc-400 border-transparent hover:text-zinc-100")
            }
            data-testid="nav-team-balance-link"
          >
            <Shuffle className="h-4 w-4" /> Team-Balance
          </NavLink>
          <NavLink
            to="/turniere"
            className={({ isActive }) =>
              "flex items-center gap-2 px-3 py-2 text-xs uppercase tracking-wide border-b-2 transition " +
              (isActive
                ? "text-brand-lighter border-brand-lighter"
                : "text-zinc-400 border-transparent hover:text-zinc-100")
            }
            data-testid="nav-tournaments-link"
          >
            <Trophy className="h-4 w-4" /> Turniere
          </NavLink>
          <NavLink
            to="/training"
            className={({ isActive }) =>
              "flex items-center gap-2 px-3 py-2 text-xs uppercase tracking-wide border-b-2 transition " +
              (isActive
                ? "text-brand-lighter border-brand-lighter"
                : "text-zinc-400 border-transparent hover:text-zinc-100")
            }
            data-testid="nav-training-link"
          >
            <Dumbbell className="h-4 w-4" /> Training
          </NavLink>
          <NavLink
            to="/leistungstest"
            className={({ isActive }) =>
              "flex items-center gap-2 px-3 py-2 text-xs uppercase tracking-wide border-b-2 transition " +
              (isActive
                ? "text-brand-lighter border-brand-lighter"
                : "text-zinc-400 border-transparent hover:text-zinc-100")
            }
            data-testid="nav-leistungstest-link"
          >
            <Activity className="h-4 w-4" /> Leistungstest
          </NavLink>
          <NavLink
            to="/noten"
            className={({ isActive }) =>
              "flex items-center gap-2 px-3 py-2 text-xs uppercase tracking-wide border-b-2 transition " +
              (isActive
                ? "text-brand-lighter border-brand-lighter"
                : "text-zinc-400 border-transparent hover:text-zinc-100")
            }
            data-testid="nav-matches-link"
          >
            <ClipboardList className="h-4 w-4" /> Spieler-Note
          </NavLink>
        </nav>
        <div className="flex items-center gap-3">
          {/* Mobile nav links removed from header — replaced by second-row mobile nav below */}
          {user?.is_super_admin && (
            <NavLink
              to="/super"
              className={({ isActive }) =>
                "flex items-center gap-1.5 px-2 py-1.5 rounded-md text-xs uppercase tracking-wider transition " +
                (isActive
                  ? "text-amber-400 bg-amber-500/10"
                  : "text-amber-400/70 hover:text-amber-400 hover:bg-zinc-900")
              }
              data-testid="nav-super-link"
            >
              <Crown className="h-3.5 w-3.5" /> <span className="hidden sm:inline">Super</span>
            </NavLink>
          )}
          {user?.role === "club_admin" && (
            <NavLink
              to="/admin"
              className={({ isActive }) =>
                "flex items-center gap-1.5 px-2 py-1.5 rounded-md text-xs uppercase tracking-wider transition " +
                (isActive
                  ? "text-brand-lighter bg-brand/10"
                  : "text-zinc-400 hover:text-brand-lighter hover:bg-zinc-900")
              }
              data-testid="nav-admin-link"
            >
              <Settings className="h-3.5 w-3.5" /> <span className="hidden sm:inline">Verwaltung</span>
            </NavLink>
          )}
          {user && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-zinc-400 hover:text-brand-lighter hover:bg-zinc-900 uppercase tracking-wider text-xs flex items-center gap-1.5"
                  data-testid="nav-user-menu-btn"
                >
                  <UserCircle className="h-4 w-4" />
                  <span className="hidden sm:inline" data-testid="nav-user-name">
                    {user.name || user.email}
                  </span>
                  <ChevronDown className="h-3 w-3 hidden sm:inline" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                className="bg-zinc-900 border-zinc-800 text-zinc-100 min-w-56"
                data-testid="nav-user-menu"
              >
                <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-zinc-500">
                  {user.email}
                </DropdownMenuLabel>
                <DropdownMenuSeparator className="bg-zinc-800" />
                {user?.is_super_admin && (
                  <>
                    <DropdownMenuItem
                      onClick={() => navigate("/super")}
                      className="hover:bg-zinc-800 focus:bg-zinc-800 cursor-pointer sm:hidden"
                      data-testid="nav-user-super-mobile"
                    >
                      <Crown className="h-4 w-4 mr-2 text-amber-400" />
                      <span className="text-sm">Super-Admin</span>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator className="bg-zinc-800 sm:hidden" />
                  </>
                )}
                <DropdownMenuItem
                  onClick={() => setPwOpen(true)}
                  className="hover:bg-zinc-800 focus:bg-zinc-800 cursor-pointer"
                  data-testid="nav-user-change-password"
                >
                  <KeyRound className="h-4 w-4 mr-2 text-brand-lighter" />
                  <span className="text-sm">Passwort ändern</span>
                </DropdownMenuItem>
                <DropdownMenuSeparator className="bg-zinc-800" />
                <DropdownMenuItem
                  onClick={handleLogout}
                  className="hover:bg-zinc-800 focus:bg-zinc-800 cursor-pointer"
                  data-testid="nav-logout-btn"
                >
                  <LogOut className="h-4 w-4 mr-2 text-zinc-400" />
                  <span className="text-sm">Abmelden</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>

      <ChangePasswordDialog open={pwOpen} onOpenChange={setPwOpen} />

      {/* Mobile-only Team-Switcher row */}
      {user?.active_team_name && (
        <div className="md:hidden border-t border-zinc-800 bg-zinc-950/90 px-4 py-2">
          {showSwitcher ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full justify-between bg-zinc-900 border-zinc-800 hover:bg-zinc-800 hover:border-brand-lighter/50 text-zinc-100 h-9 rounded-md px-3"
                  data-testid="nav-team-switcher-mobile-btn"
                >
                  <span className="flex items-center gap-2 min-w-0">
                    <Users className="h-3.5 w-3.5 text-brand-lighter shrink-0" />
                    <span className="text-xs font-semibold truncate">{user.active_team_name}</span>
                  </span>
                  <ChevronDown className="h-3.5 w-3.5 text-zinc-500 shrink-0" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="start"
                className="bg-zinc-900 border-zinc-800 text-zinc-100 min-w-[calc(100vw-2rem)]"
                data-testid="nav-team-switcher-mobile-menu"
              >
                <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-zinc-500">
                  Mannschaft wechseln
                </DropdownMenuLabel>
                <DropdownMenuSeparator className="bg-zinc-800" />
                {memberships.map((m) => (
                  <DropdownMenuItem
                    key={m.team_id}
                    onClick={() => handleSwitch(m.team_id)}
                    className="hover:bg-zinc-800 focus:bg-zinc-800 cursor-pointer"
                    data-testid={`nav-team-switch-mobile-${m.team_id}`}
                  >
                    <div className="flex items-center justify-between w-full">
                      <div>
                        <div className="text-sm font-medium">{m.team_name}</div>
                        <div className="text-[10px] text-zinc-500">{m.club_name}</div>
                      </div>
                      {m.team_id === user.active_team_id && (
                        <Check className="h-4 w-4 text-brand-lighter" />
                      )}
                    </div>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <div
              className="flex items-center gap-2 px-3 h-9 rounded-md border border-zinc-800 bg-zinc-900"
              data-testid="nav-team-label-mobile"
            >
              <Users className="h-3.5 w-3.5 text-brand-lighter shrink-0" />
              <span className="text-xs font-semibold text-zinc-100 truncate">{user.active_team_name}</span>
            </div>
          )}
        </div>
      )}

      {/* Mobile sub-nav: 5 menu items in desktop order, UPPERCASE */}
      <nav className="md:hidden border-t border-zinc-800 bg-zinc-950/90 grid grid-cols-6">
        {[
          { to: "/", end: true, label: "Spieler", Icon: Users, testid: "nav-dashboard-mobile-link" },
          { to: "/team-balance", label: "Balance", Icon: Shuffle, testid: "nav-team-balance-mobile-link" },
          { to: "/turniere", label: "Turniere", Icon: Trophy, testid: "nav-tournaments-mobile-link" },
          { to: "/training", label: "Training", Icon: Dumbbell, testid: "nav-training-mobile-link" },
          { to: "/leistungstest", label: "L.Test", Icon: Activity, testid: "nav-leistungstest-mobile-link" },
          { to: "/noten", label: "Noten", Icon: ClipboardList, testid: "nav-matches-mobile-link" },
        ].map(({ to, end, label, Icon, testid }) => (
          <NavLink
            key={to}
            to={to}
            end={end}
            className={({ isActive }) =>
              "flex flex-col items-center justify-center gap-1 py-2 text-[9px] uppercase tracking-wide font-semibold border-t-2 transition " +
              (isActive
                ? "text-brand-lighter border-brand-lighter bg-brand/5"
                : "text-zinc-400 border-transparent hover:text-zinc-100")
            }
            data-testid={testid}
          >
            <Icon className="h-4 w-4" />
            <span>{label}</span>
          </NavLink>
        ))}
      </nav>
    </header>
  );
}
